import { useState, useMemo } from "react";
import { Package } from "lucide-react";
import { RecentSyncBanner } from "@/components/inventory/RecentSyncBanner";
import {
  useNewProducts,
  useNewProductsStats,
  useHideProducts,
  useUnhideProducts,
  type NewProductsFilters,
} from "@/hooks/useNewProducts";
import { exportNewProductsToExcel } from "@/utils/excelExport";
import NewProductsStatsComponent from "@/components/new-products/NewProductsStats";
import NewProductsFiltersComponent from "@/components/new-products/NewProductsFilters";
import NewProductsBulkToolbar from "@/components/new-products/NewProductsBulkToolbar";
import NewProductsTable from "@/components/new-products/NewProductsTable";

export default function NewProducts() {
  const [filters, setFilters] = useState<NewProductsFilters>({
    search: "",
    dateFilter: "all",
    showHidden: false,
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isExporting, setIsExporting] = useState(false);

  const { data: products, isLoading: isLoadingProducts } = useNewProducts(filters);
  const { data: stats, isLoading: isLoadingStats } = useNewProductsStats();
  const hideProducts = useHideProducts();
  const unhideProducts = useUnhideProducts();

  // Check if any selected products are hidden
  const hasHiddenSelected = useMemo(() => {
    if (!products) return false;
    return products.some(p => selectedIds.has(p.id) && p.hidden);
  }, [products, selectedIds]);

  const handleSelectionChange = (id: string, selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (selected) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected && products) {
      setSelectedIds(new Set(products.map((p) => p.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleExport = async () => {
    if (!products) return;
    setIsExporting(true);
    try {
      const selectedProducts = products.filter((p) => selectedIds.has(p.id));
      exportNewProductsToExcel(selectedProducts);
    } finally {
      setIsExporting(false);
    }
  };

  const handleHide = async () => {
    await hideProducts.mutateAsync(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleUnhide = async () => {
    await unhideProducts.mutateAsync(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-primary/10 p-2">
          <Package className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Nieuwe Producten (Magazijn 2)</h1>
          <p className="text-sm text-muted-foreground">
            Overzicht van nieuw gedetecteerde producten tijdens voorraad synchronisatie
          </p>
        </div>
      </div>

      {/* Sync Status Banner */}
      <RecentSyncBanner />

      {/* Stats Cards */}
      <NewProductsStatsComponent stats={stats} isLoading={isLoadingStats} />

      {/* Filters */}
      <NewProductsFiltersComponent filters={filters} onFiltersChange={setFilters} />

      {/* Bulk Actions Toolbar */}
      <NewProductsBulkToolbar
        selectedCount={selectedIds.size}
        totalCount={products?.length || 0}
        allSelected={
          products ? products.length > 0 && products.every((p) => selectedIds.has(p.id)) : false
        }
        onSelectAll={handleSelectAll}
        onExport={handleExport}
        onHide={handleHide}
        onUnhide={handleUnhide}
        onClearSelection={handleClearSelection}
        isExporting={isExporting}
        isHiding={hideProducts.isPending || unhideProducts.isPending}
        hasHiddenSelected={hasHiddenSelected}
      />

      {/* Table */}
      <NewProductsTable
        products={products}
        isLoading={isLoadingProducts}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}
        onSelectAll={handleSelectAll}
      />
    </div>
  );
}
